/**
 * What a tenant, an owner or a buyer sees.
 *
 * A separate area of the application from everything else, with its own shell, its own credential
 * and its own client — see `lib/portal.tsx` for why the two are kept apart. Nothing here imports
 * `useSession`, and nothing here should: the person reading these screens does not work for the
 * agency and has no agency session at all.
 *
 * **The tone is different too, and deliberately.** Everywhere else the reader is a professional
 * doing their job. Here the reader is somebody who rents a flat or is three years into paying for a
 * plot, opening a link from an email, quite possibly on a phone, possibly to settle an argument
 * about whether they paid in March. So: no jargon, no abbreviations, no controls that need
 * explaining, and the answer to "what do I owe" visible without scrolling.
 *
 * **One account screen, two kinds of reader.** A tenant has a tenancy, invoices and receipts; a
 * buyer has a purchase and a payment plan; some people have both, and an owner has neither. Each
 * half is drawn only when there is something in it — a "Your purchase" heading over an empty panel
 * tells somebody they are missing something they never had.
 *
 * Signing in, registering and setting a password live in `PortalAuth.tsx`; the buyer's own screens
 * in `PortalPurchase.tsx`.
 */

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { balanceOf, formatDate, formatMoney, termSuffix, toTerm, type RentCycle } from "@rentorng/core";
import { Alert, Button, Card, CardTitle, Detail, EmptyState, Field, Input, Spinner, Table, Textarea } from "@/components/ui";
import { IconInvoice, IconKeys, IconReceipt, IconWrench, IconDoor, IconContract, IconPayout } from "@/components/icons";
import { usePageTitle } from "@/lib/title";
import { PortalProvider, usePortal } from "@/lib/portal";
import { PortalShell } from "./PortalShell";
import { PASSWORD_SET_KEY, PortalSignIn } from "./PortalAuth";
import { PurchaseSummaries, type PurchaseSummary } from "./PortalPurchase";
import { AgreementSummaries, type PortalAgreementRow } from "./PortalAgreement";
import type { InvoiceLine } from "./PortalInvoice";

/* ------------------------------------------------------------------ the account */

interface InvoiceRow {
  id: string;
  number: string;
  status: string;
  dueDate: string;
  total: number;
  paid: number;
  payToken: string | null;
  unitLabel: string | null;
  propertyName: string | null;
  /** What it is made of. Read on the invoice screen; carried here because it arrives with the list. */
  lines?: InvoiceLine[];
}

interface PaymentRow {
  id: string;
  receiptNumber: string;
  amount: number;
  method: string;
  status: string;
  receivedOn: string;
  allocations: Array<{ number: string; amount: number }>;
}

/**
 * One charge on a tenancy, besides the rent.
 *
 * `label` is the name the server sent — `chargeLabel(type, label)`, the one function that knows
 * "Other" is a default rather than a blank — and `amount` is the money, resolved from a basis that
 * may well have been a share of the rent. A tenant handed "1000" basis points has been told
 * nothing, so neither figure is re-derived here. ADR-0110, ADR-0120.
 */
interface TenancyCharge {
  type: string;
  label: string;
  basis: "fixed" | "percent_of_rent";
  value: number;
  frequency: "once" | "per_period";
  amount: number;
}

interface TenancyRow {
  id: string;
  startDate: string;
  endDate: string | null;
  status: string;
  rentAmount: number;
  rentCycle?: string;
  rentCycleEvery?: number;
  charges?: TenancyCharge[];
  unitLabel: string | null;
  propertyName: string | null;
  propertyAddress: string | null;
}

interface RequestRow {
  id: string;
  reference: string;
  title: string;
  status: string;
  createdAt: string;
  unitLabel: string | null;
}

/** Whether the offer to set a password has already been taken up, or waved away, in this browser. */
const OFFER_DISMISSED_KEY = "rentorng.portal.password.offer";

function offerHidden(): boolean {
  try {
    return Boolean(window.localStorage.getItem(PASSWORD_SET_KEY) ?? window.localStorage.getItem(OFFER_DISMISSED_KEY));
  } catch {
    /* Private mode throws. The offer appears, which costs nobody anything. */
    return false;
  }
}

function hideOffer(): void {
  try {
    window.localStorage.setItem(OFFER_DISMISSED_KEY, "1");
  } catch {
    /* See above. */
  }
}

/**
 * The offer of a password. **Offered, never nagged.**
 *
 * Whether somebody already has one is not something `/me` says, and it deliberately does not — an
 * endpoint that reports the state of a credential is an endpoint that reports the state of a
 * credential. So this is written as an offer that can be waved away for good rather than as a
 * warning that something is missing: it sits under the account rather than over it, it says what it
 * is for rather than what is wrong, and "Not now" is final in this browser.
 */
function PasswordOffer() {
  const [hidden, setHidden] = useState(() => offerHidden());
  if (hidden) return null;

  return (
    <Card className="mt-6 border-l-4 border-l-brand-500">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-md">
          <p className="flex items-center gap-2 font-semibold text-ink-900">
            <IconKeys className="size-5 text-ink-400" /> Sign in without waiting for a link
          </p>
          <p className="mt-1 text-sm text-ink-500">
            Set a password and you can come back whenever you like. Links keep working — ask for one any time you forget
            it.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/portal/password" className="btn btn-secondary btn-sm">
            Set a password
          </Link>
          <button
            type="button"
            className="text-sm text-ink-500 hover:text-ink-800"
            onClick={() => {
              hideOffer();
              setHidden(true);
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </Card>
  );
}

/**
 * The tenancy itself: what was agreed, and what it charges besides the rent.
 *
 * The portal has never drawn this. A tenant was shown a unit label in the header and an outstanding
 * figure, and the agreement underneath both — the caution deposit, the agency fee, the legal fee,
 * the levy somebody typed a name for — was on the agency's screens only. That is the whole of
 * ADR-0120's complaint: the person the charges were addressed to could not read them.
 *
 * **Every word and every figure here came from the server.** `label` is `chargeLabel(type, label)`
 * and `amount` is `resolveCharge()` — a fee quoted as a share of the rent arrives as the money it
 * will actually be. Nothing on this page falls back to a type name, because the fallback already
 * happened where the one rule lives. ADR-0110.
 *
 * Drawn only for a tenancy that is live or on notice. A tenant who moved out last year is owed
 * their invoices and their receipts; re-printing the terms of an agreement that has ended reads as
 * though it has not.
 */
function TenancyCard({ tenancy, money }: { tenancy: TenancyRow; money: (minor: number) => string }) {
  const charges = tenancy.charges ?? [];
  const term = toTerm((tenancy.rentCycle ?? "annual") as RentCycle, tenancy.rentCycleEvery);

  return (
    <Card className="mb-5" padded={false}>
      <div className="p-4 sm:p-5">
        <CardTitle hint="What you agreed to pay, and what each part of it is called.">
          <span className="inline-flex items-center gap-2">
            <IconContract className="size-5" /> Your tenancy
          </span>
        </CardTitle>
        <dl>
          <Detail label="Where">{[tenancy.unitLabel, tenancy.propertyName].filter(Boolean).join(" · ") || "—"}</Detail>
          <Detail label="Rent">
            {money(tenancy.rentAmount)} <span className="text-xs font-normal text-ink-500">{termSuffix(term)}</span>
          </Detail>
          <Detail label="From">
            {formatDate(tenancy.startDate)}
            {tenancy.endDate ? ` to ${formatDate(tenancy.endDate)}` : ""}
          </Detail>
        </dl>
      </div>

      {charges.length ? (
        <>
          <div className="border-t border-ink-200 px-4 pt-4 sm:px-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Besides the rent</p>
          </div>
          {/*
            Marked so a suite can read the names out of this table rather than off the page.

            `verify-charge-label.mjs` needs to know that the word "Other" on this screen is the name
            of a charge and not, say, a heading or an option in a control somebody never opened. The
            attribute is how it says which table it means.
          */}
          <div data-portal-charges>
            <Table
              head={
                <tr>
                  <th>Charge</th>
                  <th>When</th>
                  <th className="num">Amount</th>
                </tr>
              }
            >
              {charges.map((charge, index) => (
                <tr key={`${charge.type}-${index}`}>
                  {/* The agency's own words, exactly as the server sent them. */}
                  <td className="font-medium">{charge.label}</td>
                  <td className="text-ink-500">
                    {charge.frequency === "once" ? "Once, at the start" : "With every rent payment"}
                  </td>
                  <td className="num tabular">{money(charge.amount)}</td>
                </tr>
              ))}
            </Table>
          </div>
        </>
      ) : null}
    </Card>
  );
}

/**
 * The way through to the commission screen, for somebody who sells for the agency.
 *
 * Offered on the strength of `me.marketer`, which the server reads from the `marketers` table. The
 * alternative — fetching `/commission` and treating its 404 as "no such thing" — makes a refusal
 * into a feature switch, so a bad afternoon on the server becomes a menu that quietly disappears.
 *
 * Nothing about a figure here. What they are owed belongs on their own page, where the words
 * around it can say what it is; a number on a link is a number nobody can check.
 */
function CommissionOffer() {
  return (
    <Card className="mb-5 border-l-4 border-l-brand-500">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-md">
          <p className="flex items-center gap-2 font-semibold text-ink-900">
            <IconPayout className="size-5 text-ink-400" /> You sell for this agency
          </p>
          <p className="mt-1 text-sm text-ink-500">
            What you have earned, what has been paid to you, and what is still owed — with the sales behind each figure.
          </p>
        </div>
        <Link to="/portal/commission" className="btn btn-secondary btn-sm">
          Your commission
        </Link>
      </div>
    </Card>
  );
}

function Account() {
  const { me, get, post, signOut } = usePortal();
  usePageTitle(me?.agency?.name ? `Your account · ${me.agency.name}` : "Your account");

  const [tenancies, setTenancies] = useState<TenancyRow[] | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [payments, setPayments] = useState<PaymentRow[] | null>(null);
  const [requests, setRequests] = useState<RequestRow[] | null>(null);
  const [purchases, setPurchases] = useState<PurchaseSummary[] | null>(null);
  const [agreements, setAgreements] = useState<PortalAgreementRow[] | null>(null);
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const currency = me?.agency?.currency ?? "NGN";
  const money = (minor: number) => formatMoney(Number(minor), currency);

  const load = useMemo(
    () => async () => {
      const [t, i, p, r, b, a] = await Promise.all([
        get<{ tenancies: TenancyRow[] }>("/tenancy").catch(() => ({ tenancies: [] })),
        get<{ invoices: InvoiceRow[] }>("/invoices").catch(() => ({ invoices: [] })),
        get<{ payments: PaymentRow[] }>("/payments").catch(() => ({ payments: [] })),
        get<{ requests: RequestRow[] }>("/maintenance").catch(() => ({ requests: [] })),
        get<{ purchases: PurchaseSummary[] }>("/purchases").catch(() => ({ purchases: [] })),
        /* Agreements are asked for on every load and for every reader. A landlord is on the books
           as neither a tenant nor a buyer and would otherwise reach this screen with nothing on it
           at all — an agreement is the one thing in the portal an owner is a party to. */
        get<{ agreements: PortalAgreementRow[] }>("/agreements").catch(() => ({ agreements: [] })),
      ]);
      setTenancies(t.tenancies);
      setInvoices(i.invoices);
      setPayments(p.payments);
      setRequests(r.requests);
      setPurchases(b.purchases);
      setAgreements(a.agreements);
    },
    [get],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /* The one number this whole screen exists to answer. Computed from the invoices themselves rather
     than asked for separately, so it can never disagree with the list underneath it. */
  const outstanding = (invoices ?? []).reduce((sum, invoice) => sum + balanceOf(invoice), 0);
  const live = (tenancies ?? []).find((row) => row.status === "active" || row.status === "notice") ?? null;

  /*
    Which halves of the screen to draw.

    A buyer with no tenancy would otherwise be shown "Outstanding — nothing", an empty invoice list
    and an empty receipt list above the one thing they came for. The letting half is drawn when there
    is anything in it, and when there is nothing on either side — an owner between tenants — it is
    drawn anyway, because a screen with nothing at all on it needs somewhere to say so.
  */
  const buying = purchases ?? [];
  const letting =
    (tenancies ?? []).length > 0 || (invoices ?? []).length > 0 || (payments ?? []).length > 0 || (requests ?? []).length > 0;
  /* An agreement is something to see. Without this a landlord — no tenancy, no invoice, no
     purchase — was shown the empty-account panel underneath the very agreement they came to sign. */
  const showLetting = letting || (buying.length === 0 && (agreements ?? []).length === 0);

  const report = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setReportError(null);
    try {
      await post("/maintenance", {
        title: String(form.get("title") ?? ""),
        detail: String(form.get("detail") ?? ""),
      });
      setReporting(false);
      await load();
    } catch (problem) {
      setReportError(problem instanceof Error ? problem.message : "That could not be sent.");
    }
  };

  if (!invoices || !payments || !purchases) {
    return (
      <PortalShell agency={me?.agency}>
        <div className="flex items-center gap-3">
          <Spinner />
          <p className="text-sm text-ink-500">Loading your account…</p>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell agency={me?.agency}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{me?.name}</h1>
          {live ? (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-500">
              <IconDoor className="size-4" />
              {[live.unitLabel, live.propertyName].filter(Boolean).join(" · ")}
            </p>
          ) : null}
        </div>
        <button type="button" onClick={() => void signOut()} className="text-sm font-medium text-ink-500 hover:text-ink-800">
          Sign out
        </button>
      </div>

      {/* Somebody who sells for the agency is offered their own page, above their own affairs as a
          tenant or a buyer — they are two unrelated relationships with the same office, and the
          commission is the one they came for. Offered on `me.marketer`, never on a 404. */}
      {me?.marketer ? <CommissionOffer /> : null}

      {/* What they are buying, first, because a payment plan is the longer relationship and the one
          somebody comes back to every month. Nothing here about the scheme's other buyers, what
          else in it sold for, or how much of it is left. */}
      {buying.length ? <PurchaseSummaries purchases={buying} /> : null}

      {/* Above the money, because a signature is the thing somebody is being asked *to do* and an
          invoice is a thing to read. It draws nothing when there is nothing waiting. */}
      <AgreementSummaries agreements={agreements ?? []} />

      {showLetting ? (
        <>
          {/* What is owed, first and largest, because it is the question. */}
          <Card className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Outstanding</p>
            <p className={`tabular mt-1 text-3xl font-semibold ${outstanding > 0 ? "text-danger-700" : "text-success-700"}`}>
              {money(outstanding)}
            </p>
            <p className="mt-1 text-sm text-ink-500">
              {outstanding > 0
                ? "Across the invoices below. Open one to see what it is for and how to pay it."
                : "Nothing is outstanding on this account."}
            </p>
          </Card>

          {/* The agreement the invoices below come out of. Only while it is running: re-printing the
              terms of a tenancy that ended reads as though it has not. */}
          {live ? <TenancyCard tenancy={live} money={money} /> : null}

          <Card className="mb-5" padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <IconInvoice className="size-5" /> Invoices
                </span>
              </CardTitle>
            </div>
            {invoices.length === 0 ? (
              <EmptyState title="No invoices have been raised on this account." />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Invoice</th>
                    <th>For</th>
                    <th>Due</th>
                    <th className="num">Amount</th>
                    <th className="num">Left to pay</th>
                  </tr>
                }
              >
                {invoices.map((invoice) => {
                  const left = balanceOf(invoice);
                  return (
                    <tr key={invoice.id}>
                      {/*
                        Opens the invoice rather than the payment page.

                        It used to go straight to `/pay/…`, which asks for a sum of money and, until
                        this screen existed, said nothing whatever about what the sum was for. The
                        invoice screen answers that first and carries the same pay link on it, so
                        nobody has lost a way to pay — they have gained the chance to read what they
                        are paying before they do. An invoice with no pay token still opens, because
                        a settled or cancelled one is exactly the one somebody comes back to check.
                      */}
                      <td className="font-medium">
                        <Link className="link" to={`/portal/invoices/${invoice.id}`}>
                          {invoice.number}
                        </Link>
                      </td>
                      <td>{[invoice.unitLabel, invoice.propertyName].filter(Boolean).join(" · ") || "—"}</td>
                      <td>{formatDate(invoice.dueDate)}</td>
                      <td className="num tabular">{money(invoice.total)}</td>
                      <td className={`num tabular ${left > 0 ? "font-semibold text-danger-700" : "text-ink-400"}`}>
                        {left > 0 ? money(left) : "Paid"}
                      </td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Card>

          <Card className="mb-5" padded={false}>
            <div className="p-4 sm:p-5">
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <IconReceipt className="size-5" /> Receipts
                </span>
              </CardTitle>
            </div>
            {payments.length === 0 ? (
              <EmptyState title="No payments have been recorded on this account yet." />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Receipt</th>
                    <th>Received</th>
                    <th>Settled</th>
                    <th className="num">Amount</th>
                  </tr>
                }
              >
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="font-medium">{payment.receiptNumber}</td>
                    <td>{formatDate(payment.receivedOn)}</td>
                    {/* Which invoice each payment cleared. Without this a receipt is a figure with no
                        story, and "did my March payment go on the right thing" is unanswerable. */}
                    <td className="text-ink-500">
                      {payment.allocations.length ? payment.allocations.map((a) => a.number).join(", ") : "Held as credit"}
                    </td>
                    <td className="num tabular">{money(payment.amount)}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          <Card padded={false}>
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5">
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <IconWrench className="size-5" /> Maintenance
                </span>
              </CardTitle>
              {live ? (
                <Button variant="secondary" size="sm" onClick={() => setReporting((open) => !open)}>
                  {reporting ? "Close" : "Report a problem"}
                </Button>
              ) : null}
            </div>

            {reporting ? (
              <form onSubmit={report} className="space-y-4 border-t border-ink-200 p-4 sm:p-5">
                {reportError ? <Alert tone="danger">{reportError}</Alert> : null}
                <Field label="What is the problem?" htmlFor="title" required hint="For example: the kitchen tap is leaking.">
                  <Input id="title" name="title" required minLength={3} placeholder="e.g. The kitchen tap is leaking" />
                </Field>
                <Field label="Anything else worth knowing?" htmlFor="detail" hint="When it started, and when somebody could call.">
                  <Textarea id="detail" name="detail" rows={3} placeholder="e.g. Started on Monday. Someone is home after 4pm." />
                </Field>
                <Button type="submit">Send it to the agency</Button>
              </form>
            ) : null}

            {(requests ?? []).length === 0 ? (
              <EmptyState title="Nothing has been reported from this account." />
            ) : (
              <Table
                head={
                  <tr>
                    <th>Reference</th>
                    <th>Problem</th>
                    <th>Reported</th>
                    <th>Where it has got to</th>
                  </tr>
                }
              >
                {(requests ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="font-medium">{row.reference}</td>
                    <td>{row.title}</td>
                    <td>{formatDate(String(row.createdAt).slice(0, 10))}</td>
                    <td className="capitalize">{row.status.replace(/_/g, " ")}</td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </>
      ) : null}

      <PasswordOffer />

      {me?.agency ? (
        <p className="mt-6 text-sm text-ink-500">
          Anything else, speak to {me.agency.name}
          {me.agency.phone ? ` on ${me.agency.phone}` : ""}
          {me.agency.email ? ` or at ${me.agency.email}` : ""}.
        </p>
      ) : null}
    </PortalShell>
  );
}

/* --------------------------------------------------------------------- routing */

function PortalGate() {
  const { loading, me } = usePortal();

  if (loading) {
    return (
      <PortalShell>
        <div className="flex items-center gap-3">
          <Spinner />
          <p className="text-sm text-ink-500">Loading…</p>
        </div>
      </PortalShell>
    );
  }

  // No session, or one that has expired. Not an error — just somebody who needs a link or a password.
  return me ? <Account /> : <PortalSignIn />;
}

export function PortalPage() {
  return (
    <PortalProvider>
      <PortalGate />
    </PortalProvider>
  );
}

/*
  The other portal screens are exported from where they live. Re-exported here as well because
  `App.tsx` has imported them from this file since the portal was one screen, and an import that
  moves for no reason is a diff nobody can read.
*/
export { PortalEnterPage, PortalSetPasswordPage, PortalSignInPage, PortalSignUpPage } from "./PortalAuth";
export { PortalPurchasePage, PortalPurchaseStatementPage } from "./PortalPurchase";
